/**
 * Sentry, the error and performance monitor (ADR 0101, docs/runbooks/sentry.md).
 *
 * As with Shake, what it does is decided per environment in `src/config/env.ts` and applied
 * here verbatim. Development configures no Sentry at all, so every call below is a no-op there.
 *
 * Unlike Shake it is started before the first render, not after: it is there to catch what
 * goes wrong while the admin starts, which a reporter loaded later would never see.
 *
 * What it reports, and what it leaves out:
 *
 * - every error nothing else handled, and every query or mutation that failed (the screens
 *   turn those into a toast, which tells the admin and nobody else);
 * - except the API's own refusals. An `ApiError` below 500 is the API answering as designed
 *   (a validation problem, a 404, a session that ended), and so are the router's redirects
 *   and not-founds. A 5xx is kept: the API files its own issue for it too, and the two meet
 *   in one trace, the browser's side naming the screen;
 * - no query string on any url (the admin puts what someone typed into a search box there,
 *   routinely a customer's name or email: see `pathOnly` in `lib/shake.ts`), and none of the headers
 *   that would carry one (the Referer is the previous page's whole url); no cookies, no
 *   bodies, no IP address. The signed-in staff member is named by their id alone;
 * - no session replay. Shake records the screen where the data is anonymised and nowhere else
 *   (env.ts says why), and a second recorder would add nothing but a way round that.
 */
import * as Sentry from '@sentry/react';
import type { ErrorEvent, EventHint } from '@sentry/react';
import type { RootOptions } from 'react-dom/client';
import { isNotFound, isRedirect, type AnyRouter } from '@tanstack/react-router';

import { appConfig, appEnv, appRelease, type AppConfig, type AppEnvironment } from '@/config/env';
import { ApiError } from '@/lib/apiError';
import { isAutomatedBrowser } from '@/lib/automation';

/** An error that is the app behaving as designed, which Sentry is never told about. */
export function isExpected(error: unknown): boolean {
    if (error instanceof ApiError) return error.status < 500;
    return isRedirect(error) || isNotFound(error);
}

/** Exported for tests; app code hands it to the SDK rather than calling it. */
export function beforeSend(event: ErrorEvent, hint: EventHint): ErrorEvent | null {
    return isExpected(hint.originalException) ? null : event;
}

/** What the SDK may collect. Exported for tests. */
export const DATA_COLLECTION = {
    userInfo: false,
    cookies: false,
    urlQueryParams: false,
    httpHeaders: { request: { allow: ['User-Agent'] }, response: false },
    httpBodies: [],
} satisfies Sentry.BrowserOptions['dataCollection'];

/**
 * Starts Sentry, if this environment has a project to report to.
 *
 * The router is passed in because page loads and navigations are traced by route, not by url,
 * which is also what keeps a customer's id out of a transaction's name.
 */
export function startSentry(
    router: AnyRouter,
    config: AppConfig = appConfig,
    environment: AppEnvironment = appEnv,
    release: string | undefined = appRelease,
): void {
    const settings = config.sentry;
    if (!settings) return;

    // The end-to-end suite drives preview and staging. Its failures are Playwright's to report,
    // and a robot's thousand page loads a night would bury the people's. Same as Shake.
    if (isAutomatedBrowser()) return;

    Sentry.init({
        dsn: settings.dsn,
        environment,
        release,
        dataCollection: DATA_COLLECTION,
        integrations: [
            Sentry.tanstackRouterBrowserTracingIntegration(router),
            // The console's warnings and errors, as logs beside the issues.
            Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
        ],
        // The trace headers go to the same origin only, the SDK's default, which here is the
        // API and nothing else: a page load traced here continues through the API's side.
        tracesSampleRate: settings.tracesSampleRate,
        beforeSend,
    });
}

/**
 * React's own reports of what a render threw, for `createRoot`. Empty while Sentry is not
 * running, so the dev server keeps React's console output exactly as it was.
 */
export function rootErrorHandlers(): RootOptions {
    if (!Sentry.isInitialized()) return {};
    return {
        onUncaughtError: Sentry.reactErrorHandler(),
        onCaughtError: Sentry.reactErrorHandler(),
        onRecoverableError: Sentry.reactErrorHandler(),
    };
}

/** Reports a failed query or mutation. `beforeSend` decides whether it was expected. */
export function reportError(error: unknown): void {
    Sentry.captureException(error);
}

/** Names the signed-in staff member on what Sentry records, by their id alone. */
export function identifyStaff(id: number): void {
    Sentry.setUser({ id: String(id) });
}
